const MINTANCE_SCRIPT_ID = 'mintance-tracker';
const MINTANCE_SRC = 'https://cdn.mintance.com/libs/mintance-latest.min.js';
const MINTANCE_TOKEN = 'mnt_69EL5bTTWQOcEaWwWX09fk';
const MINTANCE_DOMAIN = 'motiv8.team';

/** Documented Mintance opt-out. Set on decline as a second line of defence. */
const MINTANCE_IGNORE_KEY = '_mnt_ignore';

const GA_SCRIPT_ID = 'ga-tracker';
const GA_MEASUREMENT_ID = 'G-ZSM33LF699';

interface MintanceFn {
    (...args: unknown[]): void;
    q?: IArguments[];
    version?: string;
}

declare global {
    interface Window {
        mintance?: MintanceFn;
        dataLayer?: unknown[];
        gtag?: (...args: unknown[]) => void;
    }
}

let loaded = false;

const appendScript = (
    id: string,
    src: string,
    attributes: Record<string, string> = {},
): void => {
    if (document.getElementById(id)) {
        return;
    }

    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.defer = true;

    Object.entries(attributes).forEach(([name, value]) => {
        script.setAttribute(name, value);
    });

    document.head.appendChild(script);
};

const loadMintance = (): void => {
    if (!window.mintance) {
        const queue: MintanceFn = function (this: unknown) {
            // eslint-disable-next-line prefer-rest-params
            (queue.q = queue.q || []).push(arguments);
        };

        window.mintance = queue;
    }

    appendScript(MINTANCE_SCRIPT_ID, MINTANCE_SRC, {
        'data-token': MINTANCE_TOKEN,
        'data-domain': MINTANCE_DOMAIN,
    });
};

const loadGoogleAnalytics = (): void => {
    if (!window.gtag) {
        const dataLayer: unknown[] = window.dataLayer || [];
        window.dataLayer = dataLayer;

        // GA reads the raw arguments object off dataLayer, not a plain array,
        // so this cannot be rewritten with rest parameters.
        const gtag: (...args: unknown[]) => void = function (this: unknown) {
            // eslint-disable-next-line prefer-rest-params
            dataLayer.push(arguments);
        };

        window.gtag = gtag;

        gtag('js', new Date());
        gtag('config', GA_MEASUREMENT_ID);
    }

    appendScript(
        GA_SCRIPT_ID,
        `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`,
    );
};

/**
 * Injects the analytics trackers. Safe to call repeatedly: guarded by both a
 * module flag and a lookup of the script ids already in the document.
 *
 * Nothing here runs until the visitor has consented (or is outside the EEA).
 * See `src/utils/consent.ts` and the CookieConsent component.
 */
export const loadTrackers = (): void => {
    if (loaded || typeof window === 'undefined') {
        return;
    }

    loaded = true;

    loadMintance();
    loadGoogleAnalytics();
};

/**
 * Records a Google Analytics pageview for a client-side route change.
 *
 * Mintance tracks SPA navigation itself by listening to pushState and popstate,
 * so it needs nothing here. GA does not: a `config` call only reports the page
 * the tracker loaded on.
 */
export const trackPageview = (path: string): void => {
    if (typeof window === 'undefined' || !window.gtag) {
        return;
    }

    window.gtag('event', 'page_view', {
        page_path: path,
        page_location: window.location.href,
        page_title: document.title,
    });
};

/** Cookies set by the two trackers, removed when consent is refused. */
const TRACKING_COOKIE_PREFIXES = ['_mnt_vid', '_mnt_sid', '_ga', '_gid'];

const clearTrackingCookies = (): void => {
    const { hostname } = window.location;

    // A cookie can only be deleted by a request that matches its domain and
    // path, and we cannot read those back. Expiring the name against both the
    // exact host and the dot-prefixed parent covers how these are actually set.
    const domains = [hostname, `.${hostname}`];

    document.cookie
        .split(';')
        .map(entry => entry.split('=')[0].trim())
        .filter(name =>
            TRACKING_COOKIE_PREFIXES.some(prefix => name.startsWith(prefix)),
        )
        .forEach(name => {
            domains.forEach(domain => {
                document.cookie = `${name}=; path=/; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
            });

            document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
        });
};

/**
 * Silences the trackers and removes what they already stored.
 *
 * A script cannot be unloaded once it is in the document, so withdrawing
 * consent after granting it relies on each vendor's documented kill switch:
 * `_mnt_ignore` for Mintance, the `ga-disable-<id>` global for GA. Existing
 * cookies are expired so that withdrawal actually removes the identifiers.
 */
export const suppressTrackers = (): void => {
    if (typeof window === 'undefined') {
        return;
    }

    (window as unknown as Record<string, boolean>)[
        `ga-disable-${GA_MEASUREMENT_ID}`
    ] = true;

    try {
        window.localStorage.setItem(MINTANCE_IGNORE_KEY, 'true');
    } catch {
        // Storage unavailable; nothing was persisted to begin with.
    }

    clearTrackingCookies();
};

/** Lifts the opt-out flags when consent is granted. */
export const unsuppressTrackers = (): void => {
    if (typeof window === 'undefined') {
        return;
    }

    (window as unknown as Record<string, boolean>)[
        `ga-disable-${GA_MEASUREMENT_ID}`
    ] = false;

    try {
        window.localStorage.removeItem(MINTANCE_IGNORE_KEY);
    } catch {
        // Storage unavailable; nothing to clear.
    }
};
